import { createElement, useCallback, useRef, useState } from 'react';
import { toast, type ToastOptions } from 'react-toastify';
import type { ApiErrorBody } from '../auth/types';
import type { AxiosError } from 'axios';
import { CredoraToastContent, type CredoraToastType } from './CredoraToastContent';
import {
  isPlanLimitApiMessage,
  openPlanLimitBlock,
  parsePlanLimitApiMessage,
} from '@/features/admin/planLimits';

const DEFAULT_OPTIONS: ToastOptions = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

function renderToast(type: CredoraToastType, message: string, options?: ToastOptions) {
  const { className: extraClass, ...rest } = options ?? {};
  return toast(createElement(CredoraToastContent, { type, message }), {
    ...DEFAULT_OPTIONS,
    ...rest,
    type,
    icon: false,
    className: ['credora-toast', `credora-toast--${type}`, extraClass].filter(Boolean).join(' '),
  });
}

export function formatApiError(err: unknown, fallback = 'Something went wrong.'): string {
  const ax = err as AxiosError<ApiErrorBody>;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item: unknown) => {
        if (typeof item === 'object' && item && 'msg' in item) {
          return String((item as { msg?: string }).msg ?? JSON.stringify(item));
        }
        return JSON.stringify(item);
      })
      .join('; ');
  }
  const body = ax?.response?.data;
  if (body?.message) {
    return body.message;
  }
  if (typeof ax?.message === 'string' && ax.message) {
    return ax.message;
  }
  return fallback;
}

function resolveMessage(input: unknown, fallback: string): string {
  if (typeof input === 'string') {
    return input;
  }
  return formatApiError(input, fallback);
}

export const credoraToast = {
  success(message: string, options?: ToastOptions) {
    return renderToast('success', message, options);
  },
  error(input: unknown, fallback = 'Something went wrong.', options?: ToastOptions) {
    return renderToast('error', resolveMessage(input, fallback), options);
  },
  warning(message: string, options?: ToastOptions) {
    return renderToast('warning', message, options);
  },
  info(message: string, options?: ToastOptions) {
    return renderToast('info', message, options);
  },
  created(entity: string, options?: ToastOptions) {
    return this.success(`${entity} created successfully.`, options);
  },
  updated(entity: string, options?: ToastOptions) {
    return this.success(`${entity} updated successfully.`, options);
  },
  deleted(entity: string, options?: ToastOptions) {
    return this.success(`${entity} deleted successfully.`, options);
  },
  saved(entity: string, options?: ToastOptions) {
    return this.success(`${entity} saved successfully.`, options);
  },
  refreshed(label = 'Data', options?: ToastOptions) {
    const text = label ? `${label} refreshed.` : 'Data refreshed.';
    return this.info(text, options);
  },
  pageLoaded(title: string, options?: ToastOptions) {
    return this.info(`Viewing ${title}.`, options);
  },
};

/** Backward-compatible alias used across RCATS features */
export const appToast = credoraToast;

export function toastApiError(err: unknown, fallback = 'Request failed.'): void {
  const message = formatApiError(err, fallback);
  if (isPlanLimitApiMessage(message)) {
    const parsed = parsePlanLimitApiMessage(message);
    if (parsed) {
      openPlanLimitBlock(parsed);
      return;
    }
  }
  credoraToast.error(message);
}

export async function runCrud<T>(
  action: () => Promise<T>,
  options: { successMessage?: string; errorMessage?: string; showSuccess?: boolean } = {},
): Promise<T> {
  const { successMessage, errorMessage = 'Operation failed.', showSuccess = true } = options;
  try {
    const result = await action();
    if (showSuccess && successMessage) {
      credoraToast.success(successMessage);
    }
    return result;
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

export async function runRefresh<T>(
  action: () => Promise<T>,
  options: { label?: string; errorMessage?: string } = {},
): Promise<T> {
  const { label = 'Data', errorMessage = 'Failed to refresh.' } = options;
  try {
    const result = await action();
    credoraToast.refreshed(label);
    return result;
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

export async function runLoad<T>(
  action: () => Promise<T>,
  options: { errorMessage?: string } = {},
): Promise<T> {
  const { errorMessage = 'Failed to load.' } = options;
  try {
    return await action();
  } catch (err) {
    credoraToast.error(err, errorMessage);
    throw err;
  }
}

export function useCredoraReloadFeedback(label = 'Data') {
  const pendingRef = useRef(false);
  const [reloadKey, setReloadKey] = useState(0);

  const requestReload = useCallback(() => {
    pendingRef.current = true;
    setReloadKey((k) => k + 1);
  }, []);

  const completeReload = useCallback(
    (err?: unknown) => {
      if (!pendingRef.current) {
        return;
      }
      pendingRef.current = false;
      if (err) {
        credoraToast.error(err, `Failed to refresh ${label.toLowerCase()}.`);
      } else {
        credoraToast.refreshed(label);
      }
    },
    [label],
  );

  return { reloadKey, requestReload, completeReload };
}
